/**
 * Role Context for Admin Role Switching
 *
 * Allows admins to simulate different user roles for testing purposes.
 * The simulated role is reset on page refresh (stored in React state only).
 *
 * Available roles:
 * - connor_admin: Full access to all features
 * - connor_employee: Standard employee access
 */
import React, { createContext, useContext, useState, useMemo } from 'react';
import type { User } from '@shared/types';

export type SimulatedRole = 'connor_admin' | 'connor_employee' | null;

interface RoleContextType {
  simulatedRole: SimulatedRole;
  setSimulatedRole: (role: SimulatedRole) => void;
  getEffectiveUser: (actualUser: User | null) => User | null;
  isSimulating: boolean;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const ROLE_OPTIONS: { value: SimulatedRole; label: string; description: string }[] = [
  { value: null, label: 'My Role', description: 'Use your actual role' },
  { value: 'connor_admin', label: 'Connor Admin', description: 'Full access to all features' },
  { value: 'connor_employee', label: 'Connor Employee', description: 'Standard employee access' },
];

export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [simulatedRole, setSimulatedRole] = useState<SimulatedRole>(null);

  const getEffectiveUser = useMemo(() => {
    return (actualUser: User | null): User | null => {
      if (!actualUser) return null;

      if (simulatedRole === null) {
        return actualUser;
      }

      const simulatedUser: User = {
        ...actualUser,
        is_connor_admin: simulatedRole === 'connor_admin',
        is_connor_employee: true, // Both roles are employees
      };

      return simulatedUser;
    };
  }, [simulatedRole]);

  const value: RoleContextType = {
    simulatedRole,
    setSimulatedRole,
    getEffectiveUser,
    isSimulating: simulatedRole !== null,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useRoleContext = () => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRoleContext must be used within a RoleProvider');
  }
  return context;
};

export default RoleContext;
